const mobile = window.matchMedia('(max-width: 989px)');
const root = document.documentElement;
const main = document.querySelector('#MainContent');
const headerGroup = document.querySelector('#header-group');
const themeColor = document.querySelector('meta[name="theme-color"]');
const initialThemeColor = themeColor?.getAttribute('content') ?? '';
const samples = new Map();
let section = null;
let revision = 0;
let frame = 0;

const updateBrowserChrome = () => {
  const scrolled = mobile.matches && window.scrollY > 1;
  root.toggleAttribute('data-browser-scrolled', scrolled);
  const style = getComputedStyle(root);
  const color = scrolled
    ? ''
    : (mobile.matches && (
        style.getPropertyValue('--browser-image-color').trim() || style.getPropertyValue('--browser-background-color').trim()
      )) || initialThemeColor;
  if (color) themeColor?.setAttribute('content', color);
  else themeColor?.removeAttribute('content');
};

const reset = () => {
  root.style.removeProperty('--browser-image-color');
  section?.classList.remove('browser-blend-section');
  updateBrowserChrome();
};

const loadSample = async (source) => {
  const sample = new Image();
  sample.crossOrigin = 'anonymous';
  sample.src = source;
  await sample.decode();
  return sample;
};

const sampleColor = async (source, image) => {
  try {
    let request = samples.get(source);
    if (!request) {
      request = loadSample(source);
      if (samples.size >= 20) samples.delete(samples.keys().next().value);
      samples.set(source, request);
    }
    const sample = await request;
    const style = getComputedStyle(image);
    const bounds = image.getBoundingClientRect();
    let width = sample.naturalWidth;
    let height = sample.naturalHeight;
    let x = 0;
    let y = 0;
    if (style.objectFit === 'cover' && bounds.width && bounds.height) {
      const scale = Math.max(bounds.width / width, bounds.height / height);
      const [positionX, positionY] = style.objectPosition.split(' ').map((value) =>
        value.endsWith('%') ? Math.min(1, Math.max(0, parseFloat(value) / 100)) : 0.5
      );
      x = (width - bounds.width / scale) * positionX;
      y = (height - bounds.height / scale) * (positionY ?? 0.5);
      width = bounds.width / scale;
      height = bounds.height / scale;
    }
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 4;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(sample, x, y, width, Math.max(1, height * 0.08), 0, 0, 32, 4);
    const { data } = context.getImageData(0, 0, 32, 4);
    const channels = [0, 0, 0];
    let weight = 0;
    for (let pixel = 0; pixel < data.length; pixel += 4) {
      const alpha = data[pixel + 3] / 255;
      weight += alpha;
      for (let channel = 0; channel < 3; channel++) channels[channel] += data[pixel + channel] * alpha;
    }
    return weight ? `rgb(${channels.map((value) => Math.round(value / weight)).join(', ')})` : null;
  } catch {
    return null;
  }
};

const update = async () => {
  frame = 0;
  const currentRevision = ++revision;
  const nextSection = main?.querySelector(':scope > .shopify-section');
  if (nextSection !== section) {
    reset();
    sectionObserver.disconnect();
    section = nextSection;
    if (section) sectionObserver.observe(section, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-browser-color-src', 'aria-hidden', 'hidden'],
    });
  }
  const hasBar = Array.from(headerGroup?.children ?? []).some((element) =>
    element.classList.contains('shopify-section') &&
    !element.classList.contains('header-section') && element.getBoundingClientRect().height > 0
  );
  if (!mobile.matches || hasBar || !headerGroup?.querySelector('.header--floating') || !section) {
    reset();
    return;
  }
  const productSection = section.querySelector('.product-information');
  let color;
  if (productSection) {
    const style = getComputedStyle(productSection);
    color = style.backgroundColor;
    if (color === 'transparent' || color === 'rgba(0, 0, 0, 0)') {
      color = style.getPropertyValue('--color-background').trim();
    }
  } else {
    const images = section.querySelectorAll('.split-hero [data-browser-color-src]');
    const image = Array.from(images).find((candidate) =>
      !candidate.closest('[hidden], [aria-hidden="true"]') && candidate.getBoundingClientRect().width > 0
    );
    const source = image?.getAttribute('data-browser-color-src');
    if (!source) {
      reset();
      return;
    }
    color = await sampleColor(source, image);
  }
  if (currentRevision !== revision) return;
  if (!color) {
    reset();
    return;
  }
  root.style.setProperty('--browser-image-color', color);
  section.classList.toggle('browser-blend-section', !productSection);
  updateBrowserChrome();
};

const schedule = () => {
  revision++;
  if (!frame) frame = requestAnimationFrame(update);
};
const sectionObserver = new MutationObserver(schedule);

if (main && headerGroup) {
  new MutationObserver(schedule).observe(main, { childList: true });
  new MutationObserver(schedule).observe(headerGroup, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['hidden', 'class'],
  });
  new ResizeObserver(schedule).observe(headerGroup);
  window.addEventListener('scroll', () => {
    const scrolled = mobile.matches && window.scrollY > 1;
    if (scrolled !== root.hasAttribute('data-browser-scrolled')) updateBrowserChrome();
  }, { passive: true });
  window.addEventListener('pageshow', updateBrowserChrome);
  window.addEventListener('resize', schedule, { passive: true });
  mobile.addEventListener('change', schedule);
  main.addEventListener('slideshow:select', schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:unload', schedule);
  schedule();
}
